import { useMemo, useRef, type ReactNode } from 'react';
import { useCapabilities } from '@lemontaigu/platform-ui';
import {
  SupabaseBoardRealtime,
  SupabaseBoardStore,
  SupabaseFeedProvenanceStore,
  SupabaseBoardWriteStore,
  SupabaseChannelReadStore,
  SupabaseChannelRealtime,
  SupabaseRosterStore,
  SupabaseMetricStore,
  SupabaseStaffingConfigStore,
  SupabaseTimesheetStore,
} from '@lemontaigu/staff/adapters/client';
import {
  createBoardDemandService,
  createBoardPresenceService,
  createMetricRegistryService,
  createStaffingConfigService,
} from '@lemontaigu/staff';
import { StaffStoresProvider } from '@lemontaigu/staff/react';
import { supabase } from '../supabase.js';

/**
 * The C3 composition root for the reception app (ladder step 6) — the
 * `PmsProvider` shape, which ARCHITECTURE.md §2 names as the reference for a
 * React SPA.
 *
 * This is the ONE place in the reception app that binds the concrete Supabase
 * client to C3's read ports. Views call `useBoard()` / `useRoster()` /
 * `useTimesheet()` from `@lemontaigu/staff/react` and depend only on the
 * interfaces; none of them may import `supabase` (E2/E3), and a test mounts
 * them over the fakes instead.
 *
 * The context and hooks themselves live in `@lemontaigu/staff/react` rather
 * than here, because all three SPAs need them — keeping them local would
 * restart the `AuthGate` duplication that step 4 removed.
 *
 * `@lemontaigu/staff/adapters` is importable here and nowhere else in the app:
 * a composition root is exactly what that subpath is for.
 */
export function BoardProvider({
  children,
  stores,
}: {
  children: ReactNode;
  /** Override seam for tests and Storybook-style harnesses; production passes nothing. */
  stores?: Parameters<typeof StaffStoresProvider>[0]['stores'];
}) {
  // The L2 gate's actor source (F3 increment 3), through a ref so it stays live
  // across the async capability fetch. Only `boardDemand` uses it: presence is
  // identity-reachable and deliberately ungated (ladder §3.19).
  const { actor } = useCapabilities();
  const actorRef = useRef(actor);
  actorRef.current = actor;

  const value = useMemo(() => {
    // ONE write store behind both driving services: they are different use-case
    // families over the same aggregate, not different aggregates.
    const boardWrites = new SupabaseBoardWriteStore(supabase);
    const actorSrc = { current: () => actorRef.current };
    return (
      stores ?? {
        board: new SupabaseBoardStore(supabase),
        // M4 — so a stopped feed cannot hide behind figures that still render
        // (RUNBOOK §20.7).
        feedProvenance: new SupabaseFeedProvenanceStore(supabase),
        roster: new SupabaseRosterStore(supabase),
        timesheet: new SupabaseTimesheetStore(supabase),
        channel: new SupabaseChannelReadStore(supabase),
        boardPresence: createBoardPresenceService(boardWrites),
        boardDemand: createBoardDemandService(boardWrites, actorSrc),
        boardRealtime: new SupabaseBoardRealtime(supabase),
        channelRealtime: new SupabaseChannelRealtime(supabase),
        // READ-ONLY here, for the « prévisionnel » strip above the Gantt
        // (2026-08-01). `load()` is deliberately ungated in the service and its
        // RLS is `rh.board.read` — which the desk has — while every WRITE on
        // the same service is gated on `rh.board.manage`, which it does not.
        // So binding it grants exactly the read and nothing else; the staffing
        // EDITOR stays operator-only in the back-office root.
        staffingConfig: createStaffingConfigService(new SupabaseStaffingConfigStore(supabase), actorSrc),
        // Same read-only bargain as staffingConfig above: the desk needs the
        // registry to render an activity's badge with its unit (ADR-0015), and
        // every write on the service is gated on `rh.board.manage`, which the
        // desk does not hold. The registry EDITOR stays in the back-office.
        metricRegistry: createMetricRegistryService(new SupabaseMetricStore(supabase), actorSrc),
      }
    );
  }, [stores]);
  return <StaffStoresProvider stores={value}>{children}</StaffStoresProvider>;
}
